// Stepper del wizard — port 1:1 de `mockups/_shared/layout.css` (.stepper / .step / .step-dot).
// Píldora por paso: dot con el número + texto corto. Estados del mock:
//   done    → fondo success suave, dot verde sólido
//   current → fondo card, borde primary, dot navy sólido
//   skipped → 40% opacidad, tachado, dot con borde punteado (contado brinca 05/06/08)
//   pending → fondo muted, dot blanco con borde
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';
import { ACCOUNT_OPENING_STEPPER_LABEL } from '../accountOpening';
import {
    ACCOUNT_OPENING_STEPS,
    isStepDone,
    isStepSkipped,
    stepIndexById,
    stepIndexBySlug,
    stepsFor,
} from '../steps';
import type { WizardSession } from '../types';

export function WizardStepper({
    session,
    currentSlug,
    accountOpening = false,
}: {
    /** Ausente en la captura de teléfono: ahí todavía no existe la venta. */
    session?: WizardSession;
    /** Slug del paso en la URL — la pantalla donde el usuario ESTÁ parado. */
    currentSlug?: string;
    /** Apertura sin sesión todavía (captura); con sesión manda su marca. */
    accountOpening?: boolean;
}) {
    const steps = stepsFor(session ?? (accountOpening ? { accountOpening: true } : undefined));
    const isOpening = steps === ACCOUNT_OPENING_STEPS;
    // "Current" es la pantalla visible y se decide por slug: dos pasos comparten el 03 del silo. En
    // el cierre (`summary`) no hay paso actual y el track queda entero en verde.
    const currentIdx = stepIndexBySlug(currentSlug, steps);

    // Lo completado lo manda el silo: `currentStep` es el último paso cuyo trabajo se confirmó.
    const doneIdx = session ? stepIndexById(session.currentStep, steps) : -1;

    const currentRef = useRef<HTMLLIElement>(null);
    // El track scrollea horizontal: sin esto el paso actual se queda fuera de vista en cuanto la
    // venta pasa de la mitad, y el vendedor deja de saber dónde va.
    useEffect(() => {
        currentRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
    }, [currentIdx]);

    return (
        <ol
            className="flex items-center gap-1 overflow-x-auto py-1"
            aria-label={isOpening ? ACCOUNT_OPENING_STEPPER_LABEL : 'Progreso de la venta'}
        >
            {steps.map((step, idx) => {
                const skipped = session ? isStepSkipped(step, session) : false;
                const isCurrent = idx === currentIdx;
                // El paso donde estás parado se pinta como actual aunque ya esté completado: eso es
                // lo que pasa al volver atrás, y "actual" es más informativo que "hecho".
                const ahead = currentIdx >= 0 && idx > currentIdx;
                const isDone =
                    !!session &&
                    !skipped &&
                    !isCurrent &&
                    !ahead &&
                    isStepDone(step, session, idx, doneIdx);

                return (
                    <li
                        key={step.slug}
                        ref={isCurrent ? currentRef : undefined}
                        aria-current={isCurrent ? 'step' : undefined}
                        title={skipped ? `${step.title}: no aplica en esta venta` : step.title}
                        className={cn(
                            'flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border border-transparent px-2.5 py-1 text-[11px] font-medium',
                            !isDone && !isCurrent && !skipped && 'bg-muted text-muted-foreground',
                            isDone && 'border-success-soft-border bg-success-soft text-success',
                            isCurrent && 'border-primary bg-card font-semibold text-foreground',
                            skipped &&
                                'bg-transparent text-muted-foreground line-through opacity-40',
                        )}
                    >
                        <span
                            className={cn(
                                'flex size-[18px] flex-none items-center justify-center rounded-full border text-[10px] font-bold',
                                !isDone &&
                                    !isCurrent &&
                                    !skipped &&
                                    'border-border bg-card text-muted-foreground',
                                isDone && 'border-success bg-success text-card',
                                isCurrent && 'border-primary bg-primary text-primary-foreground',
                                skipped &&
                                    'border-dashed border-border bg-muted text-muted-foreground',
                            )}
                        >
                            {step.label}
                        </span>
                        {step.short}
                    </li>
                );
            })}
        </ol>
    );
}
